import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import autoUploadInstructionsServices from '@src/services/autoUploadInstructions.services';

export const getInstructionsPageList = createAsyncThunk(
  'getInstructionsPageList',
  async ({ body, pageNumber, pageSize }, { getState, dispatch, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({});
      }
      const data = {
        customerId: customerId,
        ...body,
      };

      const response = await autoUploadInstructionsServices.getInstructionsList({
        data,
        pageNumber,
        pageSize,
      });

      dispatch(
        setFilterObject({
          body,
          pageNumber,
          pageSize,
        }),
      );

      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getInstructionsDetailsPageList = createAsyncThunk(
  'getInstructionsDetailsPageList',
  async ({ body, pageNumber, pageSize, filter }, { getState, dispatch, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({});
      }

      const data = {
        customerId: customerId,
        autoLoadingId: body?.autoLoadingId,
        integrationId: body?.integrationId,
        cardNumber: filter?.cardNumber,
        deliveryCode: filter?.deliveryCode,
        name: filter?.name,
        refNo: filter?.refNo,
        showOnlyPriced: filter?.showOnlyPriced,
        surname: filter?.surname,
        page: pageNumber || 1,
        pageSize: pageSize || 350,
        addressIdList: filter?.addressIdList,
      };

      const response = await autoUploadInstructionsServices.instructionsDetailList({
        data,
        pageNumber,
        pageSize,
      });

      if (
        filter &&
        (filter.name || filter.surname || filter.cardNumber || filter.refNo || filter.addressIdList)
      ) {
        dispatch(setAutoUploadFilterItems(response.data.items));
      }

      dispatch(
        setFilterObject({
          body,
          pageNumber,
          pageSize,
        }),
      );

      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getDetailsPageList = createAsyncThunk(
  'getDetailsPageList',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({});
      }

      const data = { ...body, customerId };

      return await autoUploadInstructionsServices.autoLoadingDetailList(data);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getInstructionsTempList = createAsyncThunk(
  'getInstructionsTempList',
  async (
    { pageNumber, pageSize, showOnlyPriced, autoLoadingId, integrationId },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await autoUploadInstructionsServices.instructionsTempList({
        data: { page: pageNumber || 1, pageSize: pageSize || 350 },
        showOnlyPriced,
        autoLoadingId,
        integrationId,
      });

      dispatch(
        setFilterObject({
          pageNumber,
          pageSize,
        }),
      );

      return response;
    } catch (error) {
      return rejectWithValue();
    }
  },
);

export const getByInstruction = createAsyncThunk(
  'getByInstruction',
  async (id, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({});
      }

      const res = await autoUploadInstructionsServices.getById({
        customerId,
        id,
        vouId: 1,
      });

      return res;
    } catch (error) {
      return rejectWithValue({});
    }
  },
);

export const prepareAutoLoadingSlice = createAsyncThunk(
  'getInstructionsTemp',
  async (body, { rejectWithValue }) => {
    try {
      return await autoUploadInstructionsServices.prepareAutoLoading({
        integrationId: body.integrationId,
      });
    } catch (error) {
      return rejectWithValue({});
    }
  },
);

export const getInstructionsTemp = createAsyncThunk(
  'getInstructionsTemp',
  async (_, { rejectWithValue }) => {
    try {
      return await autoUploadInstructionsServices.getInstructionsTemp();
    } catch (error) {
      return rejectWithValue({});
    }
  },
);

export const instructionDelete = createAsyncThunk(
  'instructionDelete',
  async (body, { rejectWithValue }) => {
    try {
      return await autoUploadInstructionsServices.instructionDelete(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const instructionDeleteTemp = createAsyncThunk(
  'instructionDeleteTemp',
  async (body, { rejectWithValue }) => {
    try {
      return await autoUploadInstructionsServices.instructionDeleteTemp(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const instructionUpdate = createAsyncThunk(
  'instructionUpdate',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({});
      }

      const res = await autoUploadInstructionsServices.instructionUpdate({
        vouId: 1,
        segId: 1000,
        customerId: customerId,
        ...body,
      });

      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const updateAutoLoadingCommand = createAsyncThunk(
  'updateAutoLoadingCommand',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({});
      }

      const res = await autoUploadInstructionsServices.updateAutoLoadingCommand({
        customerId: customerId,
        ...body,
      });

      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const instructionAdd = createAsyncThunk(
  'instructionAdd',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({});
      }

      body.autoLoadingCreateRequestDto.customerId = customerId;
      body.autoLoadingCreateRequestDto.vouId = 1;
      body.autoLoadingCreateRequestDto.segId = 1000;
      body.autoLoadingCreateRequestDto.isActive = true;
      try {
        body.integrationId = parseInt(body.integrationId?.toString(), 10);
      } catch (error) {
        console.error(error);
      }

      const res = await autoUploadInstructionsServices.instructionAdd({
        ...body,
      });

      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const instructionDetailPriceAdd = createAsyncThunk(
  'instructionDetailPriceAdd',
  async (body, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentInstructionId = await getState()?.autoUploadInstructions.currentInstruction?.id;
      if (!currentInstructionId) {
        return rejectWithValue({
          message: 'Kayıt bulunamadı.',
        });
      }

      const res = await autoUploadInstructionsServices.instructionDetailAdd(body);
      await dispatch(getByInstruction(currentInstructionId));

      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const instructionDetailPriceUpdate = createAsyncThunk(
  'instructionDetailPriceUpdate',
  async (body, { getState, dispatch, rejectWithValue }) => {
    try {
      return await autoUploadInstructionsServices.instructionDetailPriceUpdate(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const instructionDetailExcelUpload = createAsyncThunk(
  'instructionDetailExcelUpload',
  async (body, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentInstruction = await getState()?.autoUploadInstructions.currentInstruction;
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({
          message: 'Lütfen anasayfaya giderek müşteri tipi seçiniz.',
        });
      }

      const data = new FormData();
      data.append('FormFile', body?.file, body?.fileName);
      data.append('CustomerId', customerId);
      data.append('ExcelUploadCheck.PeriodType', currentInstruction?.periodType);
      data.append('ExcelUploadCheck.FirstRowHeaderColumn', body?.firstRowHeader);
      if (body?.cardNumber) {
        data.append('ExcelUploadCheck.CardNumberColumn', body?.cardNumber);
      }
      if (body?.refNo) {
        data.append('ExcelUploadCheck.RefNoColumn', body?.refNo);
      }
      body?.dayCount && data.append('ExcelUploadCheck.DayCountColumn', body?.dayCount);
      data.append('ExcelUploadCheck.PriceColumn', body?.price);

      const res = await autoUploadInstructionsServices.instructionExcelUpload(data);
      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const instructionDetailExcelTemplate = createAsyncThunk(
  'instructionDetailExcelTemplate',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({
          message: 'Lütfen anasayfaya giderek müşteri tipi seçiniz.',
        });
      }

      const res = await autoUploadInstructionsServices.instructionExcelDownload({
        ...body,
        customerId,
      });

      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getTotalAutoLoadingParams = createAsyncThunk(
  'getTotalAutoLoadingParams',
  async (body, { rejectWithValue }) => {
    try {
      const res = await autoUploadInstructionsServices.getTotalAutoLoadingParams({
        autoLoadingId: body?.autoLoadingId,
        integrationId: body?.integrationId,
      });
      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const instructionDetailCopy = createAsyncThunk(
  'instructionDetailCopy',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({});
      }

      body.customerId = customerId;
      body.vouId = 1;

      const res = await autoUploadInstructionsServices.instructionDetailCopy(body);

      return res;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getAutoLoadingApprovalPagedList = createAsyncThunk(
  'getAutoLoadingApprovalPagedList',
  async (body, { rejectWithValue }) => {
    try {
      return await autoUploadInstructionsServices.getAutoLoadingApprovalList({ data: body });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

const getInstructionSucceeded = (state, payload) => {
  try {
    let newPayload = {
      ...payload,
      periodType: payload?.periodType === 'Günlük' ? 0 : 1,
      confirmEveryMonth: payload?.confirmEveryMonth ? 1 : 0,
    };

    state.currentInstruction = newPayload;
  } catch (error) {}
};

const initialState = {
  selectedRowKeys: [],
  selectedAutoUploadDetails: [],
  autoDetailTableFilter: {},
  pendingApprovalList: [],
  completedId: null,
  list: [],
  detailList: [],
  instructionsTempList: [],
  tempList: [],
  autoUploadFilterItems: {},
  currentInstruction: {
    id: 0,
    periodType: 1,
    vouId: 1,
    segId: 1000,
    isActive: false,
    isStatus: false,
  },
  currentUploadInstruction: {
    autoLoadingId: 0,
    autoLoadingCreateRequestDto: {
      periodType: 1,
      confirmEveryMonth: 0,
      startDay: 0,
      isActive: true,
      orderProcessType: 0,
    },
  },
  activeStep: 0,
  instructionType: 2,
  totalCount: 0,
  totalPrice: 0,
  totalPaymentPrice: 0,
  keepAutoLoadingId: false,
  tableProperty: {
    currentPage: 1,
    page: 1,
    pageSize: 350,
    totalCount: 0,
  },
  filterObject: {
    body: {},
    pageNumber: 1,
    pageSize: 350,
  },
  detailTableProperty: {
    currentPage: 1,
    page: 1,
    pageSize: 350,
    totalCount: 0,
  },
  instructionsTempListTableProperty: {
    currentPage: 1,
    page: 1,
    pageSize: 350,
    totalCount: 0,
  },
  detailFilterObject: {
    body: {},
    pageNumber: 1,
    pageSize: 350,
  },
  clickActive: false,
  isAllSelectedRowKeys: false,
  totalAutoLoading: {
    orderProcessType: 2,
    periodType: 1,
    startDay: 0,
    confirmEveryMonth: 1,
    totalCount: 0,
    totalPrice: 0,
    totalPaymentPrice: 0,
    vouId: 1,
    segId: 1000,
    monthlyPrice: 0.0,
    dailyPrice: 0.0,
    dayCount: 0,
    paymentType: 4,
    cardToken: '',
    chooseMonth: false,
    isCheckedRadio: true,
  },
  autoLoadingId: 0,
  integrationId: 0,
};

export const autoUploadInstructionsSlice = createSlice({
  name: 'autoUploadInstructions',
  initialState,
  reducers: {
    setAutoLoadingId: (state, action) => {
      state.autoLoadingId = action.payload;
    },
    setIntegrationId: (state, action) => {
      state.integrationId = action.payload;
    },
    setKeepIntegrationId: (state, action) => {
      state.keepAutoLoadingId = action.payload;
    },
    setFilterObject: (state, action) => {
      state.filterObject = action.payload;
    },
    setTotalAutoLoading: (state, action) => {
      state.totalAutoLoading = {
        ...state.totalAutoLoading,
        ...action.payload,
      };
    },
    setActiveStep: (state, action) => {
      state.activeStep = action.payload;
    },
    nextStep: (state) => {
      state.activeStep = state.activeStep + 1;
    },
    prevStep: (state) => {
      state.activeStep = state.activeStep - 1;
    },
    setInstructionType: (state, action) => {
      state.instructionType = action.payload;
    },
    setCurrentInstruction: (state, action) => {
      state.currentInstruction = {
        ...state.currentInstruction,
        ...action.payload,
      };
    },
    setSelecteAutoUploadDetails: (state, action) => {
      state.selectedAutoUploadDetails = action.payload;
    },
    setClickActive: (state, action) => {
      state.clickActive = action.payload;
    },
    setDetailList: (state, action) => {
      state.detailList = action.payload;
    },
    setResetDetailList: (state, action) => {
      state.detailList = action.payload;
    },
    setResetInstructionsTempList: (state, action) => {
      state.instructionsTempList = action.payload;
    },
    setResetList: (state, action) => {
      state.list = action.payload;
    },
    setResetTempList: (state, action) => {
      state.tempList = action.payload;
    },
    setIsAllSelectedRowKeys: (state, action) => {
      state.isAllSelectedRowKeys = action.payload;
    },
    setSelectedRowKeys: (state, action) => {
      state.selectedRowKeys = action.payload;
    },
    setAutoLoadingNotIncludedCards: (state, action) => {
      state.autoLoadingNotIncludedCards = action.payload;
    },
    setAutoUploadFilterItems: (state, action) => {
      state.autoUploadFilterItems = action.payload;
    },
    resetAutoUploadFilterItems: (state, action) => {
      state.autoUploadFilterItems = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getByInstruction.fulfilled, (state, action) => {
      getInstructionSucceeded(state, action?.payload?.data?.items[0]);
    });
    builder.addCase(getInstructionsPageList.fulfilled, (state, action) => {
      state.list = action?.payload?.data?.items || [];
      state.tableProperty = action?.payload?.data?.pagedProperty || initialState?.tableProperty;
    });
    builder.addCase(getInstructionsDetailsPageList.fulfilled, (state, action) => {
      state.detailList = action?.payload?.data?.items || [];
      state.detailTableProperty =
        action?.payload?.data?.pagedProperty || initialState?.tableProperty;
    });
    builder.addCase(updateAutoLoadingCommand.fulfilled, (state, action) => {
      const startDay = action?.meta.arg.startDay;
      if (startDay) {
        state.totalAutoLoading = { ...state.totalAutoLoading, startDay };
      }
    });
    builder.addCase(getDetailsPageList.fulfilled, (state, action) => {
      state.detailList = action?.payload?.data?.items || [];
      state.detailTableProperty =
        action?.payload?.data?.pagedProperty || initialState?.tableProperty;
    });
    builder.addCase(getTotalAutoLoadingParams.fulfilled, (state, action) => {
      state.totalAutoLoading = {
        ...state.totalAutoLoading,
        ...action?.payload?.data,
      };
    });
    builder.addCase(instructionDeleteTemp.fulfilled, (state, action) => {
      state.keepAutoLoadingId = false;
    });
    builder.addCase(instructionAdd.fulfilled, (state, action) => {
      state.completedId = action?.payload?.data?.id;
      state.keepAutoLoadingId = false;
    });
    builder.addCase(instructionDetailCopy.fulfilled, (state, action) => {
      const { data } = action.payload;

      state.currentInstruction.confirmEveryMonth = data?.confirmEveryMonth ? 1 : 0;
      state.currentInstruction.startDay = data?.startDay === 0 ? 1 : data?.startDay;
      state.currentInstruction.id = 0;
    });
    builder.addCase(getAutoLoadingApprovalPagedList.fulfilled, (state, action) => {
      state.pendingApprovalList = action?.payload?.data?.items || [];
      state.detailTableProperty = action?.payload?.data?.pagedProperty || {};
    });
    builder.addCase(getAutoLoadingApprovalPagedList.rejected, (state, action) => {
      state.pendingApprovalList = [];
      state.detailTableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: 0,
      };
    });
  },
});

export const {
  setAutoLoadingId,
  setIntegrationId,
  setFilterObject,
  nextStep,
  setInstructionType,
  setCurrentInstruction,
  prevStep,
  setActiveStep,
  setSelecteAutoUploadDetails,
  setClickActive,
  setKeepIntegrationId,
  setDetailList,
  setResetList,
  setResetTempList,
  setResetDetailList,
  setTotalAutoLoading,
  setIsAllSelectedRowKeys,
  setSelectedRowKeys,
  setResetInstructionsTempList,
  setAutoLoadingNotIncludedCards,
  setAutoUploadFilterItems,
  resetAutoUploadFilterItems,
  setAutoDetailTableFilter,
  setAutoDetailTableFilterClear,
} = autoUploadInstructionsSlice.actions;
